import { useState, useEffect, useMemo } from "react";

import {
  BarChart3,
  CheckCircle,
  Compass,
  Files,
  Grid2x2,
  Target,
  TrendingUp,
  Upload,
  Users,
  ChartColumn,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import AccountsContainer from "../../Container/AccountsContainer";
import AssessmentsContainer from "../../Container/AssessmentsContainer";
import AssignmentContainer from "../../Container/AssignmentContainer";
import CommandCenterContainer from "../../Container/CommandCenterContainer";
import DiscoveryHub from "../../Container/DiscoveryHub";
import ExecutiveSponsorsContainer from "../../Container/ExecutiveSponsorsContainer";
import OverviewContainer from "../../Container/OverviewContainer";
import { useAppSelector } from "../../Store/Hooks";
import { selectCoverageAnalysisAccess } from "../../Store/Reducers/VendorAdminSlice";
import CoverageWorkspace from "../CoverageAnalysis/CoverageWorkspace";

import "./style.scss";

const Ops = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") || "command-center";
  const [activeTab, setActiveTab] = useState(active);
  const { resolved: coverageResolved, enabled: coverageAnalysisEnabled } =
    useAppSelector(selectCoverageAnalysisAccess);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") || "command-center";
    setActiveTab(tabFromUrl);
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabId);
    if (tabId !== "discovery-hub") {
      next.delete("hub");
    } else if (!next.get("hub")) {
      next.set("hub", "ongoing");
    }
    setSearchParams(next);
  };

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: "command-center",
        label: "Command Center",
        icon: <BarChart3 size={18} />,
      },
      {
        id: "data-upload",
        label: "Data Hub",
        icon: <Upload size={18} />,
      },
      {
        id: "accounts",
        label: "Accounts",
        icon: <Files size={18} />,
      },
      {
        id: "executive-sponsors",
        label: "Executive Sponsors",
        icon: <Users size={18} />,
      },
      {
        id: "assignments-activations",
        label: "Assignments",
        icon: <Target size={18} />,
      },
      {
        id: "assessments",
        label: "Assessments Hub",
        icon: <CheckCircle size={18} />,
      },
      {
        id: "discovery-hub",
        label: "Discovery Hub",
        icon: <Compass size={18} />,
      },
      {
        id: "outcomes",
        label: "Outcomes",
        icon: <TrendingUp size={18} />,
      },
    ];

    if (coverageResolved && coverageAnalysisEnabled) {
      const outcomesIdx = allTabs.findIndex(tab => tab.id === "outcomes");
      const coverageTab = {
        id: "coverage",
        label: "Coverage",
        icon: <Grid2x2 size={18} />,
      };
      if (outcomesIdx >= 0) {
        allTabs.splice(outcomesIdx, 0, coverageTab);
      } else {
        allTabs.push(coverageTab);
      }
    }

    return allTabs;
  }, [coverageResolved, coverageAnalysisEnabled]);

  useEffect(() => {
    if (activeTab === "leaderboard") {
      setActiveTab("command-center");
      const next = new URLSearchParams(searchParams);
      next.set("tab", "command-center");
      next.delete("hub");
      setSearchParams(next);
      return;
    }

    if (
      activeTab === "coverage" &&
      (!coverageResolved || !coverageAnalysisEnabled)
    ) {
      setActiveTab("command-center");
      const next = new URLSearchParams(searchParams);
      next.set("tab", "command-center");
      next.delete("hub");
      setSearchParams(next);
    }
  }, [
    activeTab,
    coverageResolved,
    coverageAnalysisEnabled,
    searchParams,
    setSearchParams,
  ]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewContainer />;
      case "command-center":
        return <CommandCenterContainer />;
      case "data-upload":
        return <OverviewContainer />;
      case "accounts":
        return <AccountsContainer />;
      case "executive-sponsors":
        return <ExecutiveSponsorsContainer />;
      case "assignments-activations":
        return <AssignmentContainer />;
      case "assessments":
        return <AssessmentsContainer />;
      case "discovery-hub":
        return <DiscoveryHub />;
      case "coverage":
        if (!coverageResolved || !coverageAnalysisEnabled) {
          return <CommandCenterContainer />;
        }
        return (
          <div className="flex h-[calc(100vh-13.5rem)] min-h-[32rem] flex-col overflow-hidden">
            <CoverageWorkspace />
          </div>
        );
      case "outcomes":
        return (
          <div className="w-full min-h-[65vh] bg-white rounded-xl flex items-center justify-center px-4 py-8">
            <div className="flex items-center justify-center">
              <div className="text-center bg-white rounded-2xl p-8 shadow-2xl border border-gray-200 max-w-lg mx-4">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Outcomes Coming Soon!
                </h2>
                <p className="text-gray-600 mb-6">
                  We&apos;re building comprehensive outcome tracking with
                  revenue impact analytics, sentiment analysis, and executive
                  performance metrics.
                </p>
                <div className="text-sm text-gray-500">
                  Stay tuned for updates - launching soon!
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <CommandCenterContainer />;
    }
  };

  return (
    <div className="ops-page mt-3 min-w-0 bg-white">
      {/* Page Header */}
      <PageHeader title="Executive Operations" icon={ChartColumn} />
      <div className="ops-tabs-section shrink-0 px-4 pt-4">
        <div className="flex items-center justify-between">
          <TabsComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            variant="tab-segmented"
          />
        </div>
      </div>
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Ops;
